import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  ArrowIcon,
  Button,
  Flex,
  SxProps,
  Text,
  TooltipPopper,
  useTooltip,
} from '@modusign/design-system';
import cx from 'classnames';
import { ReactNode } from 'react';
import { useDispatch } from 'react-redux';

import LoadingSpinner from '@app/components/LoadingSpinner';
import DropdownIcon from '@app/containers/DocumentDropdown/DropdownIcon';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { modalOpen } from '@app/store/modal/actions';

import './DropdownMenuRow.scss';

interface DropdownMenuRowProps extends SxProps {
  className?: string;
  title: string;
  icon?: IconProp;
  isLoading?: boolean;
  permission?: { isPermitted: boolean; message: string };
  isTabletBlocked?: boolean;
  rightIcon?: ReactNode;
  onClick?: () => void;
}

function DropdownMenuRow({
  className,
  title,
  icon,
  isLoading,
  permission,
  isTabletBlocked,
  sx,
  onClick,
  rightIcon,
}: DropdownMenuRowProps) {
  const dispatch = useDispatch();
  const blockOnClick = useResponsiveValue([isTabletBlocked, undefined, false]);

  const handleTabletBlock = () => {
    dispatch(
      modalOpen('CONFIRM', {
        title: '이용 안내',
        content:
          '화면 크기가 작아 해당 기능을 이용할 수 없습니다.\n더 큰 화면의 PC에서 이용하시길 권장합니다.',
      })
    );
  };

  const {
    activated,
    triggerRef,
    popperRef,
    getTriggerProps,
    getPopperProps,
    position,
    top,
    left,
    popperId,
  } = useTooltip({
    disabled: permission?.isPermitted !== false,
    placement: 'right',
  });

  return (
    <Flex
      as="li"
      className={cx('DropdownMenuRow', className)}
      color="textNeutralSlightlyLight"
      px={0}
      py={0}
      sx={sx}
    >
      {activated && (
        <TooltipPopper
          id={popperId}
          mode="night"
          placement="right"
          ref={popperRef}
          scale="md"
          sx={{ position, top, left }}
          {...getPopperProps()}
        >
          {permission?.message}
        </TooltipPopper>
      )}
      <Button
        bg="inherit"
        border="none"
        boxShadow="none"
        disabled={permission?.isPermitted === false}
        height="45px"
        px={7}
        py={4}
        sx={{ justifyContent: 'start' }}
        width="100%"
        onClick={blockOnClick ? handleTabletBlock : onClick}
      >
        <Flex alignItems="center" gap="6.5px" ref={triggerRef} {...getTriggerProps()}>
          {icon &&
            (isLoading ? (
              <LoadingSpinner size={14} style={{ marginRight: '11px' }} />
            ) : (
              <DropdownIcon
                icon={icon}
                sx={{
                  color:
                    permission?.isPermitted === false
                      ? 'iconNeutralSlightlyLight'
                      : 'iconPrimaryBase',
                }}
              />
            ))}
          <Text
            color={
              isLoading || permission?.isPermitted === false
                ? 'textNeutralUltraLight'
                : 'textNeutralSlightlyLight'
            }
            fontWeight="medium"
            ml={1}
          >
            {title}
          </Text>
          <ArrowIcon
            color={
              isLoading || permission?.isPermitted === false
                ? 'textNeutralUltraLight'
                : 'textNeutralSlightlyLight'
            }
            direction="right"
          />
        </Flex>
        {rightIcon}
      </Button>
    </Flex>
  );
}

export default DropdownMenuRow;
